import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/ui-bits";
import { TrendUp, Handshake, ShieldCheck, Info } from "@phosphor-icons/react";

const fmt = (v) => (v === null || v === undefined ? "—" : `${v}%`);

function MetricCard({ icon: Icon, title, your, industry, all, industryLabel }) {
  const Bar = ({ label, value, accent }) => (
    <div className="flex items-center gap-3">
      <span className="w-40 text-xs text-muted-foreground shrink-0">{label}</span>
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${Math.min(100, value || 0)}%`, background: accent }} />
      </div>
      <span className="w-14 text-right text-sm font-bold" style={{ color: accent }}>{fmt(value)}</span>
    </div>
  );
  return (
    <div className="border border-border bg-card p-6">
      <div className="flex items-center gap-2">
        <Icon size={24} weight="bold" className="text-primary" />
        <h3 className="font-heading font-bold text-lg">{title}</h3>
      </div>
      <div className="mt-5 space-y-3">
        {your != null && <Bar label="Your organization" value={your} accent="#0B3D91" />}
        <Bar label={industry?.sufficient ? `${industryLabel} average` : `${industryLabel} (insufficient data)`} value={industry?.recovery_rate ?? industry?.settlement_conversion} accent="#16A34A" />
        <Bar label={all?.sufficient ? "All industries average" : "All industries (insufficient data)"} value={all?.recovery_rate ?? all?.settlement_conversion} accent="#64748b" />
      </div>
    </div>
  );
}

export default function Benchmarks() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get("/benchmarks").then((r) => setData(r.data)).catch(() => setErr(true));
  }, []);

  if (user && !["super_admin", "client_admin", "manager", "auditor"].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div data-testid="benchmarks-page">
      <PageHeader title="Industry Benchmarks" subtitle="See how your recovery performance compares to anonymized industry averages" />

      <div className="flex items-start gap-2 border border-[#16A34A]/30 bg-[#16A34A]/5 p-4 mb-5 text-sm text-slate-700">
        <ShieldCheck size={20} weight="bold" className="text-[#16A34A] shrink-0 mt-0.5" />
        <p>Benchmarks are <strong>anonymous and aggregated</strong>. We never show tenant names, customer-level data, or tenant-specific comparisons. Group averages are hidden unless at least {data?.min_group || 3} organizations are in the group.</p>
      </div>

      {err && <p className="text-sm text-muted-foreground" data-testid="benchmarks-error">Could not load benchmarks right now.</p>}
      {!data && !err && <p className="text-sm text-muted-foreground">Loading…</p>}

      {data && (
        <div className="grid md:grid-cols-2 gap-4">
          <MetricCard
            icon={TrendUp}
            title="Recovery Rate"
            your={data.your?.recovery_rate}
            industry={data.industry}
            all={data.all}
            industryLabel={data.industry?.label || "Industry"}
          />
          <MetricCard
            icon={Handshake}
            title="Settlement Conversion"
            your={data.your?.settlement_conversion}
            industry={{ ...data.industry, recovery_rate: data.industry?.settlement_conversion }}
            all={{ ...data.all, recovery_rate: data.all?.settlement_conversion }}
            industryLabel={data.industry?.label || "Industry"}
          />
        </div>
      )}

      {data && !data.your && (
        <p className="mt-4 text-xs text-muted-foreground flex items-center gap-1.5" data-testid="benchmarks-super-note">
          <Info size={14} /> Viewing platform-wide aggregates. Your-organization figures appear when signed in under a specific tenant.
        </p>
      )}
    </div>
  );
}
